import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';

import {
  AccountActivityReturnV2,
  AccountDetails,
  AccountDocument,
  AccountRequirementStatus,
  ApplicantType,
  Cancellation,
  ConcordAccountNotesResponse,
  DataFilterDisplay,
  DeepPartial,
  DirectPayProgressMessage,
  DirectPayProjectStatus,
  EquipmentException,
  FinancingDataResponse,
  MilestoneRequirement,
  VendorEquipmentTypes,
} from 'types';

import { AccountMilestoneStipulation } from '../types/Accounts';

import { api } from './api';

export interface TransformedAccount {
  address: string;
  externalReference: string;
  friendlyName: string;
  organizationId: string;
  id: number;
  status: string;
  systemDetails: string;
  utility: string;
  programType: string;
}

type AccountResponse = any;

export interface BundleError {
  programType: string;
  reason: string;
}

export interface BundleAccountsResponse {
  accounts: Array<AccountDocument>;
  errors: Array<BundleError>;
}

interface Milestone {
  completedAt: string;
  requirements: Array<any>;
  type: string;
}

interface AccountMilestonesResponse {
  milestones: Array<Milestone>;
}

type MilestoneSummary = {
  count: number;
  type: string;
  name: string;
};

interface AccountsSummaryResponse {
  pageNum: number;
  total: number;
  milestoneSummary: {
    totalCount: number;
    milestones: Array<MilestoneSummary>;
  };
  accounts: Array<any>; // TODO: Define type
}

export const accountsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAccountWithToken: build.query<AccountDocument, any>({
      query: ({ id, inviteToken }) => ({
        url: `v2/accounts/${id}`,
        params: inviteToken ? { inviteToken } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Accounts', id }],
    }),
    getDirectPaymentInfo: build.query<
      {
        amountPastDue: string;
        totalDue: string;
        expiresAt: string | null;
        minAllowedPayment: number;
        applicantType: ApplicantType | null;
      },
      { id: string; inviteToken: string | null }
    >({
      query: ({ id, inviteToken }) => ({
        url: `accounts/${id}/customer-payment/payment-details`,
        params: inviteToken ? { inviteToken } : undefined,
      }),
    }),
    getAccount: build.query<AccountResponse, string>({
      query: (id) => ({ url: `v2/accounts/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Accounts', id }],
    }),
    getBundleGroupAccounts: build.query<{ accounts: AccountDocument[] }, string>({
      query: (accountId) => ({ url: `v2/accounts/${accountId}/bundle-group` }),
      providesTags: (_result, _error, accountId) => [{ type: 'Accounts', id: accountId }],
    }),
    getAccountDirectPayDetails: build.query<AccountResponse, string>({
      query: (id) => ({ url: `accounts/${id}/direct-pay-details` }),
      providesTags: (_result, _error, id) => [{ type: 'Accounts', id }],
    }),
    getAccountMilestones: build.query<AccountMilestonesResponse, string>({
      query: (id) => ({ url: `accounts/${id}/milestone-progress` }),
      providesTags: (_result, _error, id) => [{ type: 'AccountMilestones', id }],
    }),
    getAccountStipulations: build.query<AccountMilestoneStipulation[], string>({
      query: (accountId) => ({ url: `accounts/${accountId}/stipulations` }),
      providesTags: () => [{ type: 'AccountStipulations', id: 'LIST' }],
    }),

    getAccountsSummary: build.query<AccountsSummaryResponse, Record<any, any>>({
      query: (queryParams) => {
        return {
          url: `accounts/summary${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
      transformResponse: (response: any) => {
        const { data, pageNum, total } = response || {};
        const sortedMilestones = data?.milestoneSummary?.milestones?.toSorted(
          (mA: any, mB: any) => mA.order - mB.order,
        );
        return {
          pageNum,
          total,
          accounts: data?.accounts,
          milestoneSummary: { totalCount: data?.milestoneSummary.totalCount, milestones: sortedMilestones },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.accounts?.map(({ id }: { id: string }) => ({ type: 'Accounts' as const, id })) ?? []),
              { type: 'Accounts', id: 'LIST' },
            ]
          : [{ type: 'Accounts', id: 'LIST' }],
    }),
    checkAccountsExportLimit: build.query<any, string>({
      query: (queryParams) => ({
        url: `accounts/export/csv/precheck${queryParams}`,
      }),
    }),
    createAccountsExportRequest: build.query<any, string>({
      query: (queryParams) => ({
        url: `accounts/export/csv${queryParams}`,
        method: 'POST',
      }),
    }),
    createAccount: build.mutation<AccountResponse, AccountDetails<any>>({
      query: (account) => ({
        url: `v2/accounts`,
        method: 'POST',
        body: account,
      }),
      invalidatesTags: [{ type: 'Accounts', id: 'LIST' }],
    }),
    linkBundledAccount: build.mutation<BundleAccountsResponse, { accountId: string; bundleWith: string }>({
      query: ({ accountId, bundleWith }) => ({
        url: `v2/accounts/${accountId}/bundle`,
        method: 'POST',
        body: { bundleWith },
      }),
      invalidatesTags: (_result, _error, { accountId, bundleWith }) => [
        { type: 'Accounts', id: accountId },
        { type: 'Accounts', id: bundleWith },
        { type: 'Accounts', id: 'LIST' },
      ],
    }),
    createAccountStipulation: build.mutation<
      AccountResponse,
      {
        id: string;
        stipulation: DeepPartial<AccountMilestoneStipulation>;
      }
    >({
      query: ({ stipulation, id }) => ({
        url: `accounts/${id}/stipulations`,
        method: 'POST',
        body: stipulation,
      }),
      invalidatesTags: (result) => [
        { type: 'AccountStipulations', id: 'LIST' },
        { type: 'Accounts', id: result?.accountId },
      ],
    }),
    editAccountStipulation: build.mutation<
      AccountResponse,
      { stipulation: DeepPartial<AccountMilestoneStipulation>; stipulationId: string; accountId: string }
    >({
      query: ({ stipulation, stipulationId, accountId }) => ({
        url: `accounts/${accountId}/stipulations/${stipulationId}`,
        method: 'PATCH',
        body: stipulation,
      }),
      invalidatesTags: (result) => [
        { type: 'AccountStipulations', id: 'LIST' },
        { type: 'Accounts', id: result?.accountId },
      ],
    }),
    editAccount: build.mutation<
      AccountResponse,
      { account: DeepPartial<AccountDetails<any>>; id: string; inviteToken?: string | null }
    >({
      query: ({ account, id, inviteToken }) => ({
        url: `v2/accounts/${id}`,
        method: 'PATCH',
        params: inviteToken ? { inviteToken } : {},
        body: account,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Accounts', id },
        { type: 'Pricing', id },
        { type: 'AccountMilestones', id },
      ],
    }),
    useToggleStrandedAssetTeam: build.mutation<
      AccountResponse,
      {
        toggle: {
          ownedByStrandedAssetTeam: boolean;
        };
        id: string;
      }
    >({
      query: ({ toggle, id }) => ({
        url: `v2/accounts/${id}/stranded-fulfillment`,
        method: 'PATCH',
        body: toggle,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Accounts', id }],
    }),
    editAccountDirectPay: build.mutation<
      AccountResponse,
      {
        provider: string;
        enabled: boolean;
        id: string;
        v2?: boolean;
        subCompanyId?: string;
        override?: { reason: string; salesforceTicketNumber?: string };
      }
    >({
      query: ({ id, provider, enabled, subCompanyId, override }) => ({
        url: `v2/accounts/${id}/direct-pay`,
        method: 'PATCH',
        body: {
          provider,
          enabled,
          ...(subCompanyId ? { subCompanyId } : {}),
          ...(override ? { override } : {}),
        },
      }),
      // On the async (v2) path the PATCH resolves on a 202 with the account in a transient
      // `processing` status; write it into the cache so the badge immediately shows "Processing".
      // The SSE subscription that flips the badge to the final status is owned by a persistent
      // mounted component (which calls useGetDirectPayProgressQuery while the account is processing),
      // NOT initiated here — a one-shot dispatch from the mutation would leave no retained
      // subscriber, so its cache entry would never be torn down and could only ever fire once per
      // account. v1 returns the fully-resolved account and relies on invalidatesTags.
      async onQueryStarted({ id, v2 = false }, { dispatch, queryFulfilled }) {
        if (!v2) return;
        try {
          const { data: account } = await queryFulfilled;
          dispatch(
            accountsApi.util.updateQueryData('getAccount', id, (draft: any) => {
              Object.assign(draft, account);
            }),
          );
          // The Opt In/Opt Out buttons and the detail panel read from getAccountDirectPayDetails (a
          // separate query), so mirror what the 202 commits synchronously: the transient `processing`
          // status (otherwise the buttons stay enabled and a second opt-in/opt-out could fire before
          // the terminal SSE event) and the requested vendor alias (otherwise the panel shows the old
          // provider while getAccount already shows the new one). Provider-specific fields (projectId,
          // projectIdentifier, ship/program/parallel-payment details) are intentionally left as-is —
          // the backend keeps them at their prior values too until the async worker resolves them
          // (which may create a fresh project OR restore a prior one from history), and the terminal
          // SSE event refetches to reconcile.
          dispatch(
            accountsApi.util.updateQueryData('getAccountDirectPayDetails', id, (draft: any) => {
              if (draft && account?.directPay) {
                draft.projectStatus = account.directPay.projectStatus;
                draft.directPayMaterialVendorAlias = account.directPay.directPayMaterialVendorAlias;
                draft.subCompanyId = account.directPay.subCompanyId;
              }
            }),
          );
        } catch {
          // Mutation failed — no optimistic update to roll back.
        }
      },
      // v2 (async) path: the optimistic update above writes the processing account immediately;
      // the SSE subscription in getDirectPayProgress handles the final refetch. Skipping
      // invalidatesTags here avoids a wasted round-trip that would overwrite the optimistic write.
      // v1 (sync) path: no optimistic update — rely on invalidatesTags for the refetch.
      invalidatesTags: (_result, _error, { id, v2 }) => (v2 ? [] : [{ type: 'Accounts', id }]),
    }),
    getDirectPayProgress: build.query<null, { accountId: string }>({
      queryFn: () => ({ data: null }),
      // The query holds no real data — it exists only to own the SSE subscription lifecycle. Don't
      // retain the empty cache entry after the last subscriber unmounts, so a later processing cycle
      // for the same account opens a fresh subscription instead of hitting a stale cache entry.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded({ accountId }, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        let eventSource: EventSource | null = null;
        try {
          await cacheDataLoaded;
          eventSource = new EventSource(`/api/v2/accounts/${accountId}/direct-pay/progress`);
          eventSource.addEventListener('message', (event: MessageEvent) => {
            try {
              const message = JSON.parse(event.data) as DirectPayProgressMessage;
              if (message.status === 'completed' || message.status === 'failed') {
                // Refetch so the badge reflects the worker's final status (or rollback on failure).
                dispatch(accountsApi.util.invalidateTags([{ type: 'Accounts', id: accountId }]));
                eventSource?.close();
                eventSource = null;
              }
            } catch {
              // Ignore malformed progress messages.
            }
          });
          eventSource.addEventListener('error', () => {
            eventSource?.close();
            eventSource = null;
          });
        } catch {
          // cacheDataLoaded rejected because the entry was removed before it resolved.
        }

        await cacheEntryRemoved;
        eventSource?.close();
      },
    }),
    enrollAccountInDomesticContent: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `v2/accounts/${id}/enroll-domestic-content`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Accounts', id },
        { type: 'Quotes', id: 'LIST' },
      ],
    }),
    updateMilestoneRequirement: build.mutation<
      void,
      {
        accountId: string;
        requirementType: MilestoneRequirement;
        status: AccountRequirementStatus;
      }
    >({
      query: ({ accountId, requirementType, status }) => ({
        url: `accounts/${accountId}/requirements/${requirementType}`,
        method: 'PATCH',
        body: { requirementType, status },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: 'Accounts', id: accountId },
        { type: 'AccountMilestones', id: accountId },
      ],
    }),
    cancelAccount: build.mutation<AccountResponse, { cancelDetails: DeepPartial<Cancellation>; id: string }>({
      query: ({ cancelDetails, id }) => ({
        url: `v2/accounts/${id}/cancel`,
        method: 'PATCH',
        body: cancelDetails,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchAccount = dispatch(
          accountsApi.util.updateQueryData('getAccount', id, (draft: any) => {
            if (draft?.directPay) {
              draft.directPay.projectStatus = DirectPayProjectStatus.cancelled;
            }
          }),
        );
        const patchDirectPayDetails = dispatch(
          accountsApi.util.updateQueryData('getAccountDirectPayDetails', id, (draft: any) => {
            if (draft) {
              draft.projectStatus = DirectPayProjectStatus.cancelled;
            }
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            accountsApi.util.updateQueryData('getAccount', id, (draft: any) => {
              const optimisticProjectStatus = draft?.directPay?.projectStatus;
              Object.assign(draft, data);
              if (draft?.directPay && optimisticProjectStatus) {
                draft.directPay.projectStatus = optimisticProjectStatus;
              }
            }),
          );
          setTimeout(() => {
            dispatch(accountsApi.util.invalidateTags([{ type: 'Accounts', id }]));
          }, 5000);
        } catch {
          patchAccount.undo();
          patchDirectPayDetails.undo();
        }
      },
    }),
    reactivateAccount: build.mutation<AccountResponse, { cancelDetails: DeepPartial<Cancellation>; id: string }>({
      query: ({ cancelDetails, id }) => ({
        url: `v2/accounts/${id}/reactivate`,
        method: 'PATCH',
        body: cancelDetails,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchAccount = dispatch(
          accountsApi.util.updateQueryData('getAccount', id, (draft: any) => {
            if (draft?.directPay) {
              draft.directPay.projectStatus = DirectPayProjectStatus.active;
            }
          }),
        );
        const patchDirectPayDetails = dispatch(
          accountsApi.util.updateQueryData('getAccountDirectPayDetails', id, (draft: any) => {
            if (draft) {
              draft.projectStatus = DirectPayProjectStatus.active;
            }
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            accountsApi.util.updateQueryData('getAccount', id, (draft: any) => {
              const optimisticProjectStatus = draft?.directPay?.projectStatus;
              Object.assign(draft, data);
              if (draft?.directPay && optimisticProjectStatus) {
                draft.directPay.projectStatus = optimisticProjectStatus;
              }
            }),
          );
          setTimeout(() => {
            dispatch(accountsApi.util.invalidateTags([{ type: 'Accounts', id }]));
          }, 5000);
        } catch {
          patchAccount.undo();
          patchDirectPayDetails.undo();
        }
      },
    }),
    sendInvite: build.mutation<void, { accountId: string; email: string }>({
      query: ({ accountId, email }) => ({
        url: `accounts/${accountId}/send-invite`,
        method: 'POST',
        body: { email },
      }),
    }),
    getInviteLink: build.query<{ inviteLink?: string; userId?: string; signedUpAt?: Date }, string>({
      query: (id) => ({
        url: `accounts/${id}/app-invite`,
      }),
    }),
    getSalesRepVerificationUrl: build.query<{ url: string }, string>({
      query: (id) => ({
        url: `accounts/${id}/sales-rep-verification`,
      }),
    }),
    createSalesRepVerificationOverride: build.mutation<
      AccountResponse,
      { accountId: string; overrideReasons: string[] }
    >({
      query: ({ accountId, overrideReasons }) => ({
        url: `accounts/${accountId}/sales-rep-verification/override`,
        method: 'POST',
        body: { overrideReasons },
      }),
      invalidatesTags: (result) => [{ type: 'Accounts', id: result?.id }],
    }),
    sendSalesRepVerificationUrl: build.mutation<
      void,
      { accountId: string; salesRepEmail: string; salesRepName: string }
    >({
      query: ({ accountId, salesRepEmail, salesRepName }) => ({
        url: `accounts/${accountId}/sales-rep-verification/send`,
        method: 'POST',
        body: { salesRepEmail, salesRepName },
      }),
    }),
    getAccountActivityCursor: build.infiniteQuery<
      AccountActivityReturnV2,
      string,
      { limit: number; cursor?: string }
    >({
      infiniteQueryOptions: {
        initialPageParam: { limit: 20, cursor: undefined },
        getNextPageParam: (lastPage, _allPages, lastPageParam, _allPageParams) => {
          if (!lastPage.nextCursor) {
            return undefined;
          }
          return {
            ...lastPageParam,
            cursor: lastPage.nextCursor,
          };
        },
      },
      query: ({ queryArg, pageParam: { cursor, limit } }) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        if (cursor) {
          params.append('cursor', cursor);
        }

        const url = `v2/accounts/${queryArg}/activity${params.toString() ? `?${params.toString()}` : ''}`;
        return { url };
      },
    }),
    getAccountListFilters: build.query<any, Record<any, any>>({
      query: (queryParams) => {
        return {
          url: `accounts/filters${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
    }),
    getAccountListFilter: build.query<DataFilterDisplay, string>({
      query: (id: string) => {
        return {
          url: `accounts/filters/${id}`,
        };
      },
    }),
    getAccountAnnualConsumption: build.mutation<
      any,
      {
        accountId: string;
        annualConsumption?: Array<{ month: number; consumption: number }>;
      }
    >({
      query: ({ accountId, annualConsumption }) => {
        return {
          url: `accounts/${accountId}/annual-consumption`,
          method: 'POST',
          body: {
            annualConsumption,
          },
        };
      },
    }),
    grantEquipmentException: build.mutation<
      { equipmentException: EquipmentException },
      { accountId: string; equipmentTypes: VendorEquipmentTypes[]; note?: string }
    >({
      query: ({ accountId, equipmentTypes, note }) => ({
        url: `accounts/${accountId}/equipment-exception`,
        method: 'POST',
        body: { equipmentTypes, ...(note && { note }) },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'Accounts', id: accountId }, 'ApprovedVendors'],
      async onQueryStarted({ accountId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const patchAccount = (draft: any) => {
            draft.equipmentException = data.equipmentException;
          };
          dispatch(accountsApi.util.updateQueryData('getAccount', accountId, patchAccount));
          dispatch(accountsApi.util.updateQueryData('getAccountWithToken', { id: accountId }, patchAccount));
        } catch {
          // Tag invalidation will handle refetch on error
        }
      },
    }),
    removeEquipmentException: build.mutation<{ success: boolean }, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/equipment-exception`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'Accounts', id: accountId }, 'ApprovedVendors'],
    }),
    getFinancingData: build.query<FinancingDataResponse, string>({
      query: (accountId) => ({ url: `accounts/${accountId}/financing-data` }),
      providesTags: (_result, _error, accountId) => [{ type: 'FinancingData', id: accountId }],
    }),
    getConcordNotes: build.query<ConcordAccountNotesResponse, string>({
      query: (accountId) => ({ url: `accounts/${accountId}/concord-notes` }),
      // Notes change in Concord outside Sabal, so don't serve a stale cached copy on reopen.
      keepUnusedDataFor: 0,
    }),
    postOneTimePayment: build.mutation<
      void,
      (
        | {
            accountId: string;
            amount: number;
            paymentDate: string;
            card: {
              number: string;
              expirationMonth: string;
              expirationYear: string;
              cvc: string;
              cardHolderName: string;
              zipCode: string;
            };
          }
        | {
            accountId: string;
            amount: number;
            paymentDate: string;
            bankAccount: {
              accountNumber: string;
              routingNumber: string;
              bankAccountType: 'checking' | 'savings';
              accountHolderName: string;
            };
          }
        | { accountId: string; amount: number; paymentDate: string; paymentMethodId: string }
      ) & { inviteToken?: string | null; enrollInAutoPay?: boolean }
    >({
      query: ({ accountId, inviteToken, ...body }) => ({
        url: `accounts/${accountId}/one-time-payment`,
        method: 'POST',
        body,
        params: inviteToken ? { inviteToken } : undefined,
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: 'FinancingData', id: accountId },
        { type: 'Accounts', id: accountId },
      ],
    }),
    deletePendingPayment: build.mutation<void, { accountId: string; paymentId: string }>({
      query: ({ accountId, paymentId }) => ({
        url: `accounts/${accountId}/pending-payment/${paymentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'FinancingData', id: accountId }],
    }),
    backfillPricingSettingLockDate: build.mutation<AccountResponse, string>({
      query: (id) => ({
        url: `accounts/${id}/pricing-setting-lock-date`,
        method: 'PATCH',
      }),
    }),
    sendDirectPaymentLink: build.mutation<
      void,
      {
        accountId: string;
        applicantType: 'primary' | 'secondary';
        deliveryMethods: ('email' | 'sms')[];
        description?: string;
        minAllowedPayment?: number;
        expirationHours?: number;
      }
    >({
      query: ({ accountId, applicantType, deliveryMethods, description, minAllowedPayment, expirationHours }) => ({
        url: `accounts/${accountId}/customer-payment/send-payment-link`,
        method: 'POST',
        body: {
          applicantType,
          deliveryMethods,
          ...(description ? { description } : {}),
          ...(minAllowedPayment !== undefined ? { minAllowedPayment } : {}),
          ...(expirationHours !== undefined ? { expirationHours } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'Accounts', id: accountId }],
    }),
    deactivateDirectPaymentLink: build.mutation<void, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `accounts/${accountId}/customer-payment/deactivate-payment-link`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'Accounts', id: accountId }],
    }),
  }),
});

export const {
  useGetAccountQuery,
  useGetBundleGroupAccountsQuery,
  useGetAccountDirectPayDetailsQuery,
  useGetAccountWithTokenQuery,
  useGetDirectPaymentInfoQuery,
  useGetAccountMilestonesQuery,
  useGetAccountStipulationsQuery,
  useGetAccountsSummaryQuery,
  useLazyCheckAccountsExportLimitQuery,
  useLazyCreateAccountsExportRequestQuery,
  useLazyGetSalesRepVerificationUrlQuery,
  useCreateSalesRepVerificationOverrideMutation,
  useLazyGetAccountsSummaryQuery,
  useCancelAccountMutation,
  useReactivateAccountMutation,
  useCreateAccountMutation,
  useLinkBundledAccountMutation,
  useCreateAccountStipulationMutation,
  useEditAccountMutation,
  useUseToggleStrandedAssetTeamMutation,
  useEditAccountStipulationMutation,
  useSendInviteMutation,
  useSendSalesRepVerificationUrlMutation,
  useLazyGetInviteLinkQuery,
  useGetAccountListFiltersQuery,
  useUpdateMilestoneRequirementMutation,
  useEditAccountDirectPayMutation: useEditAccountDirectPayMutationInternal,
  useEnrollAccountInDomesticContentMutation,
  useGetAccountListFilterQuery,
  useGetAccountActivityCursorInfiniteQuery,
  useGetAccountAnnualConsumptionMutation,
  useGrantEquipmentExceptionMutation,
  useRemoveEquipmentExceptionMutation,
  usePostOneTimePaymentMutation,
  useDeletePendingPaymentMutation,
  useGetConcordNotesQuery,
  useGetFinancingDataQuery,
  useBackfillPricingSettingLockDateMutation,
  useSendDirectPaymentLinkMutation,
  useDeactivateDirectPaymentLinkMutation,
  useGetDirectPayProgressQuery,
} = accountsApi;

/**
 * LaunchDarkly kill-switch for the v2 async direct-pay opt-in/opt-out flow.
 *
 * Default is `false` (synchronous PATCH) so flipping the flag off — or losing the LD connection —
 * reverts to the legacy blocking behavior with no SSE progress channel.
 */
export const useDirectPayAsyncEnabled = (): boolean => {
  const { directPayAsyncEnabled = false } = useFlags();
  return directPayAsyncEnabled;
};

/**
 * Public edit-direct-pay mutation hook. Reads the `directPayAsyncEnabled` LaunchDarkly flag and
 * injects the `v2` arg into every trigger call so the mutation opens the SSE progress channel on
 * the async path. Call sites stay unaware of the v1/v2 split.
 */
export const useEditAccountDirectPayMutation = ((
  options?: Parameters<typeof useEditAccountDirectPayMutationInternal>[0],
) => {
  const v2 = useDirectPayAsyncEnabled();
  const [trigger, result] = useEditAccountDirectPayMutationInternal(options);
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useEditAccountDirectPayMutationInternal>;
}) as typeof useEditAccountDirectPayMutationInternal;
